// api/common/WorkerHubClient.ts
import * as signalR from '@microsoft/signalr';
import { API_BASE_URL } from '../config';

/**
 * Minimal direct SignalR client for `WorkerHub` (/hubs/worker) — same reasoning as
 * `MessagesHub.ts`: not built on `SignalRServiceBase`, which is unverified/never-exercised
 * elsewhere in this codebase. Wires whichever `WorkerHub` events the caller actually needs
 * (only pass the handlers you use) — `Assigned`/`Arrived`/`Completed` still have no publisher
 * call site (see `WorkerRealtimePublisher`'s doc comment backend-side) even though
 * `PaymentReleased` and `LocationUpdated` do now.
 */
export interface PaymentReleasedPayload {
  payoutId: string;
  amount: number;
  periodStart: string;
  periodEnd: string;
}

export interface LocationUpdatedPayload {
  workerId: string;
  latitude: number;
  longitude: number;
  at: string;
}

export interface WorkerHubHandlers {
  onPaymentReleased?: (payload: PaymentReleasedPayload) => void;
  /** Dispatcher-only in practice — the backend only ever sends this to manager/admin groups
   * (see `WorkerRealtimePublisher.LocationUpdatedAsync`), never to the worker's own group. */
  onLocationUpdated?: (payload: LocationUpdatedPayload) => void;
}

export function connectWorkerHub(accessToken: string, handlers: WorkerHubHandlers): signalR.HubConnection {
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(`${API_BASE_URL}/hubs/worker`, { accessTokenFactory: () => accessToken })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  if (handlers.onPaymentReleased) connection.on('OnPaymentReleased', handlers.onPaymentReleased);
  if (handlers.onLocationUpdated) connection.on('OnLocationUpdated', handlers.onLocationUpdated);

  connection.start().catch((err) => {
    console.error('[WorkerHubClient] connection failed:', err);
  });

  return connection;
}
